import axios from 'axios';
import { buildApiUrl, getApiBaseUrl } from '../../config/api';
import {
    getRequest,
    getStructuresSuccess,
    getFeeRecordsSuccess,
    getStudentFeeSuccess,
    getSingleRecordSuccess,
    getSummarySuccess,
    getDefaultersSuccess,
    getCollectionReportSuccess,
    getClassReportSuccess,
    getAuditLogsSuccess,
    getPaymentSubmissionsSuccess,
    clearFeeRecordDetail,
    stuffDone,
    getFailed,
    getError,
    underFeeControl, // eslint-disable-line no-unused-vars
} from './feeSlice';

export const getAllFeeStructures = (schoolId) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.get(`${getApiBaseUrl()}/FeeStructures/${schoolId}`);
        if (Array.isArray(result.data)) {
            dispatch(getStructuresSuccess(result.data));
        } else if (result.data?.message === "No fee structures found") {
            dispatch(getStructuresSuccess([]));
        } else if (result.data?.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getStructuresSuccess([]));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

function dispatchBulkFeeStructureResult(dispatch, data) {
    const summary = data?.summary || {};
    const created = Number(summary.created) || 0;
    const skipped = Number(summary.skipped) || 0;
    const failed = Number(summary.failed) || 0;
    const msg = data?.message || "";

    if (failed > 0 && created === 0) {
        dispatch(getFailed(msg || "Could not create fee structures."));
        return;
    }
    const assigned = Number(summary.feeRecordsAssigned) || 0;

    if (created > 0 || assigned > 0) {
        dispatch(stuffDone(msg || `Successfully created ${created} fee structure(s).`));
        return;
    }
    if (skipped > 0) {
        dispatch(
            stuffDone(
                msg ||
                    "All classes in this scope already have a fee structure for this term."
            )
        );
        return;
    }
    dispatch(getFailed(msg || "No fee structures were created."));
}

export const createFeeStructure = (data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.post(`${getApiBaseUrl()}/FeeStructure`, data);
        if (result.data?.bulk) {
            dispatchBulkFeeStructureResult(dispatch, result.data);
        } else if (result.data.message && !result.data.id && !result.data._id) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(
                stuffDone(
                    "Fee structure saved successfully and assigned to students in that class."
                )
            );
        }
    } catch (error) {
        const data = error.response?.data;
        if (data?.bulk) {
            dispatchBulkFeeStructureResult(dispatch, data);
        } else {
            dispatch(
                getFailed(
                    typeof data === "object" && data?.message
                        ? data.message
                        : error.message || "Request failed"
                )
            );
        }
    }
};

export const syncFeeAssignments = (schoolId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.post(`${getApiBaseUrl()}/FeeStructure/sync-assignments`, {
            schoolId,
            ...filters,
        });
        const msg = result.data?.message;
        const assigned = Number(result.data?.feeRecordsAssigned) || 0;
        if (assigned > 0 || (msg && !result.data?.error)) {
            dispatch(stuffDone(msg || "Fee records synced to students."));
            dispatch(getAllFeeStructures(schoolId));
            dispatch(getFeeSummary(schoolId));
        } else if (msg) {
            dispatch(stuffDone(msg));
        } else {
            dispatch(stuffDone("Fee sync completed."));
        }
    } catch (error) {
        dispatch(
            getFailed(
                error.response?.data?.message || error.message || "Fee sync failed"
            )
        );
    }
};

export const updateFeeStructure = (id, data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.put(`${getApiBaseUrl()}/FeeStructure/${id}`, data, {
            headers: { 'Content-Type': 'application/json' },
        });
        if (result.data.message && !result.data.id && !result.data._id) {
            dispatch(getFailed(result.data.message));
        } else {
            const ca = result.data?.classAssignment;
            const n = ca?.created ?? 0;
            dispatch(
                stuffDone(
                    n > 0
                        ? `Fee structure updated. Assigned or updated fees for ${n} student(s).`
                        : "Fee structure updated."
                )
            );
        }
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const deleteFeeStructure = (id) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.delete(`${getApiBaseUrl()}/FeeStructure/${id}`);
        if (
            result.data?.message ===
            "Fee structure deactivated and unpaid records removed"
        ) {
            dispatch(stuffDone());
        } else if (result.data?.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const assignFeeToClass = (data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.post(`${getApiBaseUrl()}/FeeRecord/assign-class`, data);
        if (result.data?.message === "Fee assignment completed") {
            dispatch(stuffDone());
            if (data?.schoolId) {
                dispatch(getAllFeeStructures(data.schoolId));
                dispatch(getFeeSummary(data.schoolId));
            }
        } else if (result.data?.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const assignFeeToStudent = (data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.post(`${getApiBaseUrl()}/FeeRecord/assign-student`, data);
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const getAllFeeRecords = (schoolId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const params = new URLSearchParams(filters).toString();
        const result = await axios.get(
            `${getApiBaseUrl()}/FeeRecords/${schoolId}?${params}`
        );
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getFeeRecordsSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const getStudentFeeRecords = (studentId) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.get(`${getApiBaseUrl()}/FeeRecord/student/${studentId}`);
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getStudentFeeSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const getFeeRecordById = (id) => async (dispatch) => {
    dispatch(clearFeeRecordDetail());
    dispatch(getRequest());

    try {
        const result = await axios.get(`${getApiBaseUrl()}/FeeRecord/${id}`);
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getSingleRecordSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const recordPayment = (recordId, paymentData) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.post(
            `${getApiBaseUrl()}/FeeRecord/${recordId}/payment`,
            paymentData
        );
        if (result.data.message === 'Payment recorded successfully') {
            dispatch(stuffDone());
            const schoolId = paymentData?.schoolId;
            if (schoolId) {
                dispatch(getFeeSummary(schoolId, { recordsScope: "current" }));
                dispatch(getAllFeeStructures(schoolId));
            }
        } else {
            dispatch(getFailed(result.data.message));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const voidPayment = (recordId, receiptNumber, data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.put(
            `${getApiBaseUrl()}/FeeRecord/${recordId}/payment/${receiptNumber}/void`,
            data,
            {
                headers: { 'Content-Type': 'application/json' },
            }
        );
        if (result.data?.message === "Payment voided") {
            dispatch(stuffDone());
        } else if (result.data?.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const getReceiptData = (recordId, receiptNumber) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.get(
            `${getApiBaseUrl()}/FeeRecord/${recordId}/receipt/${receiptNumber}`
        );
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getSingleRecordSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const applyWaiver = (recordId, data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.put(
            `${getApiBaseUrl()}/FeeRecord/${recordId}/waiver`,
            data,
            {
                headers: { 'Content-Type': 'application/json' },
            }
        );
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const getFeeSummary = (schoolId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const params = new URLSearchParams(filters).toString();
        const result = await axios.get(
            buildApiUrl(`/FeeReport/summary/${schoolId}?${params}`)
        );
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getSummarySuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const getDefaulters = (schoolId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const params = new URLSearchParams(filters).toString();
        const result = await axios.get(
            buildApiUrl(`/FeeReport/defaulters/${schoolId}?${params}`)
        );
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getDefaultersSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const getCollectionReport = (schoolId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const params = new URLSearchParams(filters).toString();
        const result = await axios.get(
            buildApiUrl(`/FeeReport/collections/${schoolId}?${params}`)
        );
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getCollectionReportSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const getClassFeeReport = (classId) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.get(buildApiUrl(`/FeeReport/class/${classId}`));
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getClassReportSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const getFeeAuditLogs = (schoolId, params = {}) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const q = new URLSearchParams(params).toString();
        const result = await axios.get(buildApiUrl(`/FeeAudit/${schoolId}?${q}`));
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else if (Array.isArray(result.data.logs)) {
            dispatch(getAuditLogsSuccess(result.data.logs));
        } else {
            dispatch(getAuditLogsSuccess([]));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const recordFeeReminder = (recordId, body) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.post(
            buildApiUrl(`/FeeRecord/${recordId}/reminder`),
            body || {},
            {
                headers: { "Content-Type": "application/json" },
            }
        );
        const msg = result.data?.message || "";
        if (
            msg === "Reminder logged" ||
            msg === "SMS reminder sent" ||
            msg.startsWith("Reminder logged")
        ) {
            dispatch(stuffDone(msg));
        } else if (result.data?.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone("Reminder saved"));
        }
    } catch (error) {
        dispatch(getError(error));
    }
};

export const bulkAssignFees = (payload) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.post(buildApiUrl(`/FeeBulk/assign-students`), payload, {
            headers: { "Content-Type": "application/json" },
        });
        if (result.data?.message === "Bulk assignment finished") {
            dispatch(stuffDone());
            return result.data;
        }
        if (result.data?.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
        return null;
    } catch (error) {
        dispatch(getError(error));
        return null;
    }
};

export const getPendingFeeSubmissions = (schoolId) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.get(
            `${getApiBaseUrl()}/FeePaymentSubmission/school/${schoolId}`,
            { params: { status: "Pending" } },
        );
        dispatch(getPaymentSubmissionsSuccess(result.data?.submissions || []));
    } catch (error) {
        dispatch(getError(error));
    }
};

export const submitFeePaymentProof = (payload) => async () => {
    return submitFeePaymentProofRequest(payload);
};

/** Plain async helper for student payment proof (no Redux). Sends multipart file upload. */
export async function submitFeePaymentProofRequest(payload) {
    const { proofFile, ...fields } = payload || {};
    if (!proofFile) {
        return { ok: false, message: "Payment proof is required. Upload a photo or PDF of your receipt." };
    }

    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
        if (value != null && value !== "") form.append(key, String(value));
    });
    form.append("proof", proofFile);

    try {
        const result = await axios.post(`${getApiBaseUrl()}/FeePaymentSubmission`, form);
        if (result.data?.message && !result.data?.submission) {
            return { ok: false, message: result.data.message };
        }
        return { ok: true, data: result.data };
    } catch (error) {
        return {
            ok: false,
            message:
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                "Could not submit payment",
        };
    }
}

export const approveFeeSubmission = (id, body) => async () => {
    try {
        const result = await axios.put(`${getApiBaseUrl()}/FeePaymentSubmission/${id}/approve`, body);
        if (result.data?.message && !result.data?.receiptNumber && !result.data?.submission) {
            return { ok: false, message: result.data.message };
        }
        return { ok: true, data: result.data };
    } catch (error) {
        return {
            ok: false,
            message: error.response?.data?.message || error.message || "Could not approve payment",
        };
    }
};

export const rejectFeeSubmission = (id, body) => async () => {
    try {
        const result = await axios.put(`${getApiBaseUrl()}/FeePaymentSubmission/${id}/reject`, body);
        if (result.data?.message && result.data.message.includes("Could not")) {
            return { ok: false, message: result.data.message };
        }
        return { ok: true, data: result.data };
    } catch (error) {
        return {
            ok: false,
            message: error.response?.data?.message || error.message || "Could not reject payment",
        };
    }
};
